import { useEffect, useRef } from 'react'
import { useLocation } from 'react-router-dom'
import { Box, useMantineColorScheme } from '@mantine/core'
import { useAuth } from '@/contexts/AuthContext'
import { BreadcrumbProvider } from '@/contexts/BreadcrumbContext'
import AppRoutes from '@/routes/AppRoutes'
import { changeLanguage } from '@/services/languageService'
import AppBreadcrumbs from './Breadcrumbs'
import AgFooter from './Footer'
import AgHeader from './Header'
import ReportButton from './ReportButton'

// Routes that should be full-screen without header/footer
const FULLSCREEN_ROUTES = ['/', '/login']

export default function AgAppShell() {
  const location = useLocation()
  const { me } = useAuth()
  const isFullscreen = FULLSCREEN_ROUTES.includes(location.pathname)
  const { colorScheme } = useMantineColorScheme()
  const isDark = colorScheme === 'dark'
  const prevMe = useRef<string | null>(null)

  // Apply user's language preference on login (null → user transition)
  useEffect(() => {
    const prevId = prevMe.current
    const curId = me?.id ?? null
    prevMe.current = curId
    if (!prevId && curId && me?.languagePreference) {
      changeLanguage(me.languagePreference, false)
    }
  }, [me?.id])

  // For fullscreen pages (like public map, login), render without shell
  if (isFullscreen) {
    return <AppRoutes />
  }

  return (
    <BreadcrumbProvider>
    <Box
      style={{
        display: 'flex',
        flexDirection: 'column',
        minHeight: '100vh',
        background: isDark
          ? `
            radial-gradient(ellipse at 20% 20%, rgba(0, 255, 0, 0.06) 0%, transparent 50%),
            radial-gradient(ellipse at 80% 80%, rgba(0, 180, 0, 0.04) 0%, transparent 50%),
            linear-gradient(180deg, #0a100a 0%, #050a05 50%, #0a100a 100%)
          `
          : `
            radial-gradient(ellipse at 20% 20%, rgba(0, 180, 0, 0.04) 0%, transparent 50%),
            radial-gradient(ellipse at 80% 80%, rgba(0, 150, 0, 0.03) 0%, transparent 50%),
            linear-gradient(180deg, #f8faf8 0%, #f0f4f0 50%, #f8faf8 100%)
          `,
      }}
    >
      {/* Header */}
      <AgHeader />
      <AppBreadcrumbs />

      {/* Main content */}
      <Box
        style={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          position: 'relative',
        }}
      >
        {/* Grid pattern overlay */}
        <Box
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundImage: isDark
              ? `
                linear-gradient(rgba(0, 255, 0, 0.05) 1px, transparent 1px),
                linear-gradient(90deg, rgba(0, 255, 0, 0.05) 1px, transparent 1px)
              `
              : `
                linear-gradient(rgba(0, 150, 0, 0.03) 1px, transparent 1px),
                linear-gradient(90deg, rgba(0, 150, 0, 0.03) 1px, transparent 1px)
              `,
            backgroundSize: '40px 40px',
            pointerEvents: 'none',
          }}
        />
        <AppRoutes />
      </Box>

      <AgFooter />
      <ReportButton />
    </Box>
    </BreadcrumbProvider>
  )
}
